import { useMemo, useState, type ReactNode } from 'react';
import { useFormik, FormikProvider, Form as FormikForm, type FormikHelpers } from 'formik';
import { FormContextProvider, useForm } from './FormContext';
import { BlockNavigationPrompt } from './BlockNavigationPrompt';
import { getYupFieldLabels, getYupRequiredFields } from '~/util/yup';
import { FormikDevTools } from './form-dev-tools/FormikDevTool';
import { useMutation } from '~/util/http/useMutation';
import type {
	FormFieldCalculationOptions,
	FormHelpers,
	FormikChildren,
	FormMutationProps,
	FormStatus,
	FormValidationSchema,
	FormValues
} from './types';
import { FormError } from './FormErrors';
import { useRegisterForm } from './FormRegistryContext';

function FormRegistration({ id, formHelpers }: { id: string; formHelpers: FormHelpers }) {
	useRegisterForm(id, formHelpers);
	return null;
}

export function FormChildren({ children }: { children: FormikChildren }) {
	const formHelpers = useForm();
	if (typeof children === 'function') {
		return children(formHelpers.formHelpers);
	}
	return children;
}

export interface FormProps {
	//runs after a successful submission, receives the transformed values, the result from the mutation (if applicable), and the pretransformed values (if applicable)
	onSuccess?: (values: FormValues, result?: unknown, pretransformedValues?: FormValues) => void;
	initialValues: FormValues;
	validationSchema?: FormValidationSchema;
	readOnly?: boolean;
	saveMutation?: FormMutationProps;
	children: ReactNode | ((formik: FormHelpers) => ReactNode);
	onSaveTransform?: (values: FormValues) => FormValues;

	className?: string | undefined;
	debug?: boolean;
	showValues?: boolean;
	fieldCalculation?: FormFieldCalculationOptions;

	errorAction?: {
		errorActionText: string;
		errorAction: () => void;
	};

	registryId?: string;
}

export function Form({
	initialValues,
	validationSchema,
	readOnly = false,
	saveMutation,
	onSuccess,
	onSaveTransform,
	className,
	debug = false,
	showValues = false,
	fieldCalculation,
	errorAction,
	registryId,
	children
}: FormProps) {
	const [submissionState, setSubmissionState] = useState<{
		formStatus: FormStatus;
		submissionError?: {
			title: string;
			detail?: string;
			errors?: Record<string, string[]>;
		};
	}>({ formStatus: 'initial' });

	const navBlockerActive = submissionState.formStatus === 'initial' || submissionState.formStatus === 'error';

	//Dummy data to use the mutation hook if no saveMutation is provided
	const onSave = useMutation({
		...(saveMutation ? saveMutation : { url: '', method: 'POST' }),
		notifyError: false
	});

	const yupDetails = useMemo(() => {
		if (!validationSchema) {
			return undefined;
		}

		const labels = getYupFieldLabels(validationSchema);
		const requiredFields = getYupRequiredFields(validationSchema);
		return {
			labels,
			requiredFields
		};
	}, [validationSchema]);

	const handleDebugSubmit = (values: FormValues, transformedValues: FormValues) => {
		console.log({
			values,
			transformedValues
		});
		formik.setSubmitting(false);
		setSubmissionState(prev => ({ ...prev, formStatus: 'submitted' }));
	};

	const handleNoSaveMutation = (values: FormValues, transformedValues: FormValues) => {
		formik.setSubmitting(false);
		setSubmissionState(prev => ({ ...prev, formStatus: 'submitted' }));
		onSuccess?.(transformedValues, undefined, values);
		return;
	};

	const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
		formikHelpers.setSubmitting(true);
		const transformedValues = onSaveTransform ? onSaveTransform({ ...values }) : values;
		if (debug) {
			handleDebugSubmit(values, transformedValues);
			return;
		}

		if (!saveMutation) {
			handleNoSaveMutation(values, transformedValues);
			return;
		}

		setSubmissionState(prev => ({ ...prev, formStatus: 'submitting' }));

		onSave.mutate(transformedValues, {
			onSuccess: result => {
				formik.setSubmitting(false);
				setSubmissionState({ formStatus: 'submitted' });
				onSuccess?.(transformedValues, result, values);
			},
			onError: error => {
				formik.setSubmitting(false);
				setSubmissionState({
					formStatus: 'error',
					submissionError: {
						title: error.response?.data.title || 'An error occurred while saving.',
						detail: error.response?.data.detail,
						errors: error.response?.data?.errors
					}
				});
			}
		});
	};

	const formik = useFormik<FormValues>({
		initialValues,
		validationSchema,
		onSubmit: handleSubmit,
		enableReinitialize: false,
		validateOnMount: true,
		validateOnBlur: true,
		validateOnChange: false
	});

	const displayOnSubmitErrors = formik.submitCount > 0 && !formik.isValid;
	const displayServerErrors = !displayOnSubmitErrors && submissionState.formStatus === 'error' && !!submissionState.submissionError;

	const errorElement =
		formik.errors && Object.keys(formik.errors).length > 0 ? (
			<FormError message="Please fix the errors below and try again." errors={formik.errors} {...errorAction} />
		) : submissionState.submissionError ? (
			<FormError message={submissionState.submissionError.title} {...submissionState.submissionError} {...errorAction} />
		) : null;

	const errorCount =
		formik.errors && Object.keys(formik.errors).length > 0
			? Object.keys(formik.errors).length
			: submissionState.submissionError
			? submissionState.submissionError.errors
				? Object.keys(submissionState.submissionError.errors).length
				: 1
			: 0;

	const formHelpers: FormHelpers = {
		...formik,
		displayErrorElement: displayOnSubmitErrors || displayServerErrors,
		errorElement,
		errorCount,
		readOnly,
		fieldLabels: yupDetails?.labels,
		requiredFields: yupDetails?.requiredFields,
		hasUnsavedChanges: formik.dirty && !formik.isSubmitting,
		canBeReset: formik.dirty && !formik.isSubmitting && !readOnly,
		canBeSubmitted: formik.isValid && !readOnly && !formik.isSubmitting,
		formStatus: submissionState.formStatus,
		submitForm: async () => {
			formik.submitForm();
		},
		fieldCalculation,
		hasErrors: !!formik.errors && Object.keys(formik.errors).length > 0,
		isSubmitting: onSave.isPending
	};

	return (
		<FormikProvider value={formik}>
			<FormContextProvider initialValues={initialValues} formHelpers={formHelpers}>
				{registryId && <FormRegistration id={registryId} formHelpers={formHelpers} />}
				<FormikForm className={className ?? 'flex min-h-full flex-col'}>
					<FormChildren>{children}</FormChildren>
				</FormikForm>
				{(debug || showValues) && <FormikDevTools />}
				{!registryId && navBlockerActive && formHelpers.hasUnsavedChanges && <BlockNavigationPrompt />}
			</FormContextProvider>
		</FormikProvider>
	);
}
